import React from 'react';

import { PlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import type { LinkProps } from '@vlabs/monokit/shared/ui/link';
import { Link } from '@vlabs/monokit/shared/ui/link';

import { CreateSourceGroupForm } from './CreateSourceGroupForm';
import { useCreateSourceGroup } from './useCreateSourceGroup';
import { useCreateSourceGroupTranslation } from '../i18n';
import type { CreateSourceGroupProps } from '../types';

const displayName = 'CreateSourceGroupButton';
const testid = buildTestid(displayName);

interface CreateSourceGroupButtonCommonProps extends
  CreateSourceGroupProps,
  WithDataTestid,
  Omit<ButtonProps, 'onClick'> {}

// Ссылочный вариант (as=Link) требует to — путь строится вызывающей стороной
type CreateSourceGroupButtonProps = CreateSourceGroupButtonCommonProps & (
  | { as?: undefined; to?: never }
  | { as: typeof Link; to: LinkProps['to'] }
);

function CreateSourceGroupButton({
  onCreateSourceGroup = undefined,
  'data-testid': dataTestId = undefined,
  as = undefined,
  to = undefined,
  ...props
}: CreateSourceGroupButtonProps): React.JSX.Element {
  const { t } = useCreateSourceGroupTranslation();
  const modal = useModal({ size: 'small', scrollable: true });
  const createSourceGroup = useCreateSourceGroup({
    onCreateSourceGroup,
    onSuccess: modal.close,
  });

  const triggerProps = {
    children: t('title'),
    onClick: modal.open,
    ...testid(dataTestId),
    ...props,
  };

  let trigger: React.JSX.Element;
  if (as === Link) {
    trigger = (
      <Link
        {...triggerProps}
        to={to}
      />
    );
  } else {
    trigger = (
      <Button
        icon={<PlusIcon />}
        {...triggerProps}
      />
    );
  }

  return (
    <React.Fragment>
      {modal.wrap(
        <CreateSourceGroupForm
          isPending={createSourceGroup.isPending}
          onCancel={modal.close}
          onSubmit={createSourceGroup.onSubmit}
        />,
      )}

      {trigger}
    </React.Fragment>
  );
}

CreateSourceGroupButton.displayName = displayName;

export { CreateSourceGroupButton };
